import PropTypes from "prop-types";
import React, { useState, useEffect } from "react";
import { FileManager } from "@valikwp/react-file-manager";
import {
  Box,
  Flex,
  Text,
  Title,
  Modal,
  LoadingOverlay,
  useMantineTheme,
  Alert,
  Loader,
  CopyButton,
  Tooltip,
  ActionIcon,
  TextInput,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import "@valikwp/react-file-manager/dist/style.css";
import { useStylesFileManager } from "../../utils/mantine/useStylesFileManager";
import { useStylesUserTabContent } from "../../utils/mantine/useStylesUserTabContent";
import { useStylesIcons } from "../../utils/mantine/useStylesIcons";

import CustomButton from "../mantineCustomComponents/CustomButton";
import CustomAnchor from "../mantineCustomComponents/CustomAnchor";
import { formatFileSize } from "../../utils/formatFileSize";
import { formatHexStr } from "../../utils/formatHexStr";
import DownloadPDFUserFound from "../DownloadPDFUserFound";
import {
  formatTimeStamp,
  LONG_DISPLAY_FORMAT,
} from "../../utils/collectionFound/dateTimeUtils";
import { verifyHashForS3File } from "../../utils/verifyHashForS3File";
import { buildVerifyStampPath } from "../../utils/verifyStampLink";
import LogoBlockchainSvg from "../../svg/LogoBlockchainSvg";
import LogoPDFSvg from "../../svg/LogoPDFSvg";
import { createVerifiedFileDownload, URLs } from "../../utils/apis";

import {
  IconRosetteDiscountCheckFilled,
  IconMessageReportFilled,
  IconCheck,
  IconCopy,
} from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import {
  FILE_SIZE_WARNING_BYTES,
  FILE_SIZE_HARD_LIMIT_MESSAGE,
  FILE_SIZE_HARD_LIMIT_TITLE,
  FILE_SIZE_WARNING_MESSAGE,
  FILE_SIZE_WARNING_TITLE,
} from "../../utils/fileSizeConstants";

export const transformData = (backendFiles) => {
  const result = [];
  const directories = new Set();

  backendFiles.forEach((item) => {
    const rawParts = item.Key.split("/").filter(Boolean);
    const filePathFull = `/${rawParts.join("/")}`; // Keep original Key for download API
    const userAddress = rawParts[0];
    // Backend: no collection → user/files/file; with collection → user/collections/collection_name/file. Hide "files" and "collections" in UI.
    const pathParts = rawParts
      .slice(1)
      .filter((p) => p !== "stamped" && p !== "files" && p !== "collections");

    let currentPath = "";

    for (let i = 0; i < pathParts.length - 1; i++) {
      currentPath += `/${pathParts[i]}`;

      const link =
        userAddress === pathParts[i]
          ? ""
          : `/verify/user-data/?user=${userAddress}&collection_name=${pathParts[i]}`;

      if (!directories.has(currentPath)) {
        const directory = {
          name: pathParts[i],
          isCollection: i === 0,
          isDirectory: true,
          path: currentPath,
          link,
        };
        result.push(directory);
        directories.add(currentPath);
      }
    }

    const filePath = `/${pathParts.join("/")}`;
    const fileName = pathParts[pathParts.length - 1];
    const fileParts = fileName.split(".");
    const fileExtension = fileParts.length > 1 ? fileParts.pop() : "";
    const collectionName = pathParts.length > 1 ? pathParts[0] : "";

    const file = {
      name: fileName,
      isDirectory: false,
      path: filePath,
      fullPath: filePathFull,
      updatedAt: item.LastModified,
      size: item.Size,
      type: fileExtension,
      metadata: item.Metadata,
      collectionName,
    };
    result.push(file);
  });

  return result;
};

const Folders = ({ timeZone, dataStorage }) => {
  // Mantine theme instance to apply custom styles and theming throughout the component
  const theme = useMantineTheme();

  const [visible, { toggle, close: closeLoader }] = useDisclosure(false);

  // Modal Preview
  const [opened, { open, close }] = useDisclosure(false);

  const closeModal = () => {
    setResponseData({});
    close();
  };

  const [selectedFile, setSelectedFile] = useState(null);

  const [stampData, setStampData] = useState(null);

  const openModal = (file) => {
    if (file.isDirectory === false) {
      setSelectedFile(file);
      open();
      setStampData(null);
      verifyHashForS3File(file?.metadata?.object_cid, setStampData);
    }
  };

  //css classes
  const { classes: classesFileManager } = useStylesFileManager();
  const { classes: classesUserTabContent } = useStylesUserTabContent();

  const { classes: classesIcons } = useStylesIcons();

  const [files, setFiles] = useState([]);

  const [isLoading, setIsLoading] = useState(true); // Loading state

  const [loadingCreateLink, setLoadingCreateLink] = useState(false); // Loading create file link

  const [responseData, setResponseData] = useState({});
  const [pendingDownloadFolder, setPendingDownloadFolder] = useState(null);

  useEffect(() => {
    const fetchFiles = async () => {
      try {
        const response = await fetch("/storage/list/");
        if (!response.ok)
          throw new Error(`HTTP error! Status: ${response.status}`);

        const data = await response.json();
        if (!Array.isArray(data.files)) {
          console.error("Error: data.files is not an array", data.files);
          return;
        }

        const transformedFiles = transformData(data.files);
        setFiles(transformedFiles);
      } catch (err) {
        console.log(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFiles();
  }, []);

  useEffect(() => {}, [files]);

  const handleDownload = async (file) => {
    try {
      const urlDownload = `/storage/download/?file_name=${encodeURIComponent(
        file.fullPath,
      )}`;
      const response = await fetch(urlDownload, { method: "GET" });
      if (!response.ok)
        throw new Error(`Failed to download file: ${file.name}`);

      const data = await response.json();

      const link = document.createElement("a");
      link.href = data.url;
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error downloading file:", error);
    }
  };

  const handleCreateLinkFile = async (fileData) => {
    setResponseData({});
    const ResponseData = await createVerifiedFileDownload(
      fileData,
      setLoadingCreateLink,
    );
    setResponseData(ResponseData.data);
  };

  // Determines the correct backend subfolder path for downloading a collection.
  // Returns "collections/name" for new path format or just "name" for legacy format
  // based on the presence of "/collections/" in file paths.
  const getSubfolderForDownload = (displayCollectionName, fileList) => {
    const useNewPath = fileList.some(
      (f) =>
        f.collectionName === displayCollectionName &&
        f.fullPath?.includes("/collections/"),
    );
    return useNewPath
      ? `collections/${displayCollectionName}`
      : displayCollectionName;
  };

  const downloadZipDirect = async (folder) => {
    const subfolder = getSubfolderForDownload(folder, files);
    const urlDownload = `/storage/download-zip/?subfolder=${encodeURIComponent(
      subfolder,
    )}`;
    const response = await fetch(urlDownload, { method: "GET" });

    if (!response.ok) throw new Error(`Failed to download file: ${folder}`);

    const data = await response.json();
    if (!data.url) {
      throw new Error("Download URL is missing.");
    }

    const link = document.createElement("a");
    link.href = data.url;
    link.download = folder;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Download folder zip
  const handleDownloadZip = async (folder) => {
    toggle();
    try {
      // Second click after warning: go straight to download
      if (pendingDownloadFolder === folder) {
        await downloadZipDirect(folder);
        setPendingDownloadFolder(null);
        return;
      }

      const subfolder = getSubfolderForDownload(folder, files);
      const urlDownload = `/storage/download-zip/?subfolder=${encodeURIComponent(
        subfolder,
      )}&metadata=true`;
      const response = await fetch(urlDownload, { method: "GET" });

      if (!response.ok) throw new Error(`Failed to download file: ${folder}`);

      const data = await response.json();

      const totalBytes = data.total_bytes || 0;
      const overHardLimit = data.over_hard_limit;
      const warningLimit = FILE_SIZE_WARNING_BYTES;

      if (overHardLimit) {
        notifications.show({
          title: FILE_SIZE_HARD_LIMIT_TITLE,
          message: FILE_SIZE_HARD_LIMIT_MESSAGE,
          color: "red",
        });
        setPendingDownloadFolder(null);
        return;
      }

      if (totalBytes >= warningLimit) {
        notifications.show({
          title: FILE_SIZE_WARNING_TITLE,
          message: FILE_SIZE_WARNING_MESSAGE,
          color: "yellow",
        });
        setPendingDownloadFolder(folder);
        return;
      }

      // Under warning threshold: download immediately
      await downloadZipDirect(folder);
      setPendingDownloadFolder(null);
    } catch (error) {
      console.error("Error downloading file:", error);
      notifications.show({
        title: "Error",
        message: error.message || "Failed to download ZIP",
        color: "red",
      });
    } finally {
      closeLoader();
    }
  };

  if (isLoading) {
    return (
      <Box>
        <Flex
          align={theme.flex.align.center}
          justify={theme.flex.justify.center}
          styles={() => ({
            root: {
              minHeight: 400,
            },
          })}
        >
          Loading...
        </Flex>
      </Box>
    ); // Show a loading message while data is being fetched
  }
  const initialPath =
    files.length > 0 ? formatHexStr(files[0].path, 7, 4, 14) : "";

  return (
    <Box
      className={`${classesFileManager.fileManager} ${classesUserTabContent.tabBox}`}
      pos="relative"
    >
      <LoadingOverlay
        visible={visible}
        loaderProps={{ children: "Loading..." }}
      />

      {selectedFile && (
        <Modal
          className={classesFileManager.filePreviewWrap}
          opened={opened}
          onClose={closeModal}
          title="Stamped File Details"
        >
          <Box p={theme.padding.baseEm}>
            <Flex
              className={classesFileManager.item}
              gap={theme.flex.gap.xs}
              align={theme.flex.align.center}
            >
              <Text className={classesFileManager.key}>File name: </Text>
              <Text
                className={`${classesFileManager.value} ${classesFileManager.filePreview}`}
              >
                {formatHexStr(selectedFile.name, 260, 0, 260)}
              </Text>
            </Flex>
            <Flex
              gap={theme.flex.gap.xs}
              className={classesFileManager.item}
              align={theme.flex.align.center}
            >
              <Text className={classesFileManager.key}>File size: </Text>
              <Text
                className={`${classesFileManager.value} ${classesFileManager.filePreview}`}
              >
                {formatFileSize(selectedFile.size)}
              </Text>
            </Flex>

            {selectedFile.collectionName && (
              <Flex
                gap={theme.flex.gap.xs}
                className={classesFileManager.item}
                align={theme.flex.align.center}
              >
                <Text className={classesFileManager.key}>
                  Collection Name:{" "}
                </Text>
                <Text
                  className={`${classesFileManager.value} ${classesFileManager.filePreview}`}
                >
                  {selectedFile.collectionName}
                </Text>
              </Flex>
            )}

            {selectedFile?.metadata?.timestamp && (
              <Flex
                gap={theme.flex.gap.xs}
                className={classesFileManager.item}
                align={theme.flex.align.center}
              >
                <Text className={classesFileManager.key}>
                  Stamp Date ({timeZone}):{" "}
                </Text>
                <Text
                  className={`${classesFileManager.value} ${classesFileManager.filePreview}`}
                >
                  {formatTimeStamp(
                    selectedFile?.metadata?.timestamp,
                    timeZone,
                    LONG_DISPLAY_FORMAT,
                  )}
                </Text>
              </Flex>
            )}
            <Box>
              <CustomButton
                text="Download File"
                componentClasses="link download-file"
                variant="transparent"
                onClick={() => handleDownload(selectedFile)}
              ></CustomButton>
            </Box>
            {selectedFile?.collectionName && (
              <Box>
                <CustomButton
                  text="Get Verified Download Link"
                  componentClasses="link"
                  variant="transparent"
                  onClick={() => handleCreateLinkFile(selectedFile)}
                ></CustomButton>
              </Box>
            )}
            {loadingCreateLink && (
              <Loader className={classesFileManager.linkLoader} />
            )}

            {responseData?.success && (
              <Alert
                variant="transparent"
                title="Success"
                className={classesFileManager.alert}
                icon={<IconRosetteDiscountCheckFilled />}
              >
                <Flex className={classesFileManager.alertBlock}>
                  {" "}
                  <TextInput
                    classNames={{
                      root: classesFileManager.alertInput,
                    }}
                    disabled={true}
                    value={`${window.location.origin}${URLs.VERIFY_URL}?link-id=${responseData?.data.link_id}`}
                  />
                  <CopyButton
                    value={`${window.location.origin}${URLs.VERIFY_URL}?link-id=${responseData?.data.link_id}`}
                    timeout={2000}
                  >
                    {({ copied, copy }) => (
                      <Tooltip
                        label={copied ? "Copied" : "Copy"}
                        className={classesIcons.copy}
                        withArrow
                        position="right"
                      >
                        <ActionIcon
                          color={
                            copied
                              ? theme.customColors.actionIconFirst
                              : theme.customColors.actionIconSecond
                          }
                          variant="subtle"
                          onClick={copy}
                        >
                          {copied ? (
                            <IconCheck className={classesIcons.width} />
                          ) : (
                            <IconCopy className={classesIcons.width} />
                          )}
                        </ActionIcon>
                      </Tooltip>
                    )}
                  </CopyButton>
                </Flex>
              </Alert>
            )}

            {responseData?.error && (
              <Alert
                variant="transparent"
                className={`${classesFileManager.alert} ${classesFileManager.alertRed}`}
                title="Error"
                icon={<IconMessageReportFilled />}
              >
                {responseData.error}
              </Alert>
            )}

            {selectedFile.collectionName && (
              <Box>
                <CustomButton
                  text="Download Collection"
                  componentClasses="link"
                  variant="transparent"
                  onClick={() => handleDownloadZip(selectedFile.collectionName)}
                ></CustomButton>
              </Box>
            )}

            <Flex gap={theme.flex.gap.base}>
              {selectedFile?.metadata?.object_cid && (
                <>
                  <Text className={classesFileManager.key}>
                    Stamp Receipts:{" "}
                  </Text>
                  <CustomAnchor
                    text=""
                    componentClasses=""
                    title="vBase Stamp Verification"
                    href={buildVerifyStampPath({
                      objectCid:
                        stampData?.objectCid ||
                        selectedFile?.metadata?.object_cid,
                      transactionHash: stampData?.transactionHash,
                      collectionCid: stampData?.collection_hash,
                    })}
                    target="_blank"
                    rel="noopener noreferrer"
                    LogoVBSvgEnable="true"
                  ></CustomAnchor>
                </>
              )}

              {stampData ? (
                <>
                  <CustomAnchor
                    text=""
                    componentClasses=""
                    title="Public Blockchain Record"
                    href={`${stampData.blockExplorerUrl}${stampData.transactionHash}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    LogoBlockchainSvgEnable="true"
                  ></CustomAnchor>
                  <DownloadPDFUserFound
                    user_id={stampData.user_id}
                    user={stampData.user}
                    collection_name={stampData.collection_name}
                    collection_hash={stampData.collection_hash}
                    objectCid={stampData.objectCid}
                    blockExplorerUrl={stampData.blockExplorerUrl}
                    transactionHash={stampData.transactionHash}
                    chainId={stampData.chainId}
                    blockchainName={stampData.blockchainName}
                    formattedDateTime={
                      formatTimeStamp(
                        stampData.timestamp,
                        stampData.display_timezone,
                        LONG_DISPLAY_FORMAT,
                      ) +
                      " " +
                      timeZone
                    }
                  />
                </>
              ) : (
                <>
                  {selectedFile?.metadata?.object_cid && (
                    <Flex gap={theme.flex.gap.base} opacity="0.5">
                      <CustomAnchor
                        text=""
                        componentClasses=""
                        title="Please wait, the link is being generated..."
                        href="#user_data_storage"
                        rel="noopener noreferrer"
                        LogoBlockchainSvgEnable="true"
                      >
                        <LogoBlockchainSvg />
                      </CustomAnchor>

                      <CustomAnchor
                        text=""
                        componentClasses=""
                        title="Please wait, the PDF is being generated..."
                        href="#user_data_storage"
                        rel="noopener noreferrer"
                        LogoPDFSvgEnable="true"
                      >
                        <LogoPDFSvg />
                      </CustomAnchor>
                    </Flex>
                  )}
                </>
              )}
            </Flex>
          </Box>
        </Modal>
      )}

      <Title order={2}>Browse Stamped Data</Title>
      <Text className="text" pb={theme.padding.lgEm}>
        A backup of your previously stamped data, organized by Collection
      </Text>
      {files.length > 0 ? (
        <>
          <FileManager
            files={files}
            enableFilePreview={false}
            onFileOpen={openModal}
            layout="list"
            initialPath={initialPath}
            onDownload={handleDownload}
            onDownloadZip={handleDownloadZip}
            onPaste={() => {}}
            timeZone={timeZone}
          />
          <Text className="text" pt={theme.padding.lgEm}>
            Files you stamp via the web app are {dataStorage !== "s3" && "not"}{" "}
            currently stored. To change this setting,{" "}
            <CustomAnchor
              text="click here"
              componentClasses="link"
              href="/profile/#account_settings"
            ></CustomAnchor>
            .
          </Text>
        </>
      ) : (
        <Flex
          justify={theme.flex.justify.center}
          align={theme.flex.align.center}
          h="10em"
        >
          <Text>No files available.</Text>
        </Flex>
      )}
    </Box>
  );
};

Folders.propTypes = {
  timeZone: PropTypes.string.isRequired,
  dataStorage: PropTypes.string,
  file: PropTypes.shape({
    fullPath: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  }),
};

export default Folders;
